import { type CreditCardDialogContext, type CreditCardDialogFlowActions } from '@cart-services/hooks/credit-card-dialog-types'
import { useCreditCardDialog } from '@cart-services/hooks/use-credit-card-dialog'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { Button } from '@pets-ui/Button/Button'
import { Checkbox } from '@pets-ui/Checkbox/Checkbox'
import { Dialog } from '@pets-ui/Dialog/Dialog'
import { DialogActions } from '@pets-ui/DialogActions/DialogActions'
import { DialogContent } from '@pets-ui/DialogContent/DialogContent'
import { DialogHeader } from '@pets-ui/DialogHeader/DialogHeader'
import { Drawer } from '@pets-ui/Drawer/Drawer'
import { Skeleton } from '@pets-ui/Skeleton/Skeleton'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PaymentFallbackIcon from '../assets/payment_fallback.svg?react'
import { DialogPaymentLeaveConfirm } from '../DialogPaymentLeaveConfirm/DialogPaymentLeaveConfirm'
import cartUiI18nInstance from '../i18n/i18nConfig'

/** loading skeleton 佔位高度，貼近卡片容器以減少切換抖動。 */
const CARD_FIELDS_MIN_HEIGHT_PX = 240

const styles = stylex.create({
  mount: {
    width: '100%',
  },
  /** SDK 內容出現後才收起沒被選中的空容器（避免白佔 VStack gap）；此時另一個容器已有內容，不會誤收正在用的那個。 */
  collapseWhenEmpty: {
    display: {
      default: 'block',
      ':empty': 'none',
    },
  },
  contentNoBottomPadding: {
    paddingBottom: 0,
  },
  drawerPaper: {
    borderRadius: '8px 8px 0px 0px',
  },
  errorBox: {
    borderRadius: 8,
    backgroundColor: colors.grey100,
    paddingBottom: 24,
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 24,
    width: '100%',
  },
  errorText: {
    width: '100%',
  },
})

export interface DialogCreditCardConfirmProps extends CreditCardDialogFlowActions {
  context: CreditCardDialogContext
  onClose: () => void
  onError: (error: unknown) => void
  /** 由消費線監聽 `paymentModule.onValidationChange` 持有；付款鈕恆 enabled，點擊時據此決定送出或提示錯誤。 */
  isCreditCardValid: boolean
  /** 卡片無效時的點擊處理：觸發 SDK 顯示欄位錯誤而非送出（SDK 對無效卡不回 callback，直接送會卡死）。 */
  revealCardValidationErrors: () => void
  /** 未傳則不顯示「記住卡片」；其他線尚未接線前維持原樣。 */
  canRememberPaymentCardToken?: boolean
  /** 消費線回寫 `context.rememberPaymentCardToken`，本元件據此重掛以新旗標重跑 init。 */
  onRememberPaymentCardTokenChange?: (checked: boolean) => void
  disabledChangeRememberPaymentCard?: boolean
  /** 文案由消費線傳入，重用各站台既有 i18n。 */
  rememberCardLabel?: string
  /** 使用者確認離開付款時呼叫（離開確認框點「離開」）。 */
  onCancel?: () => void
}

export const DialogCreditCardConfirm = ({ context, onClose, mountCreditCardFields, submitAndExecute, cancelInFlight, onError, isCreditCardValid, revealCardValidationErrors, canRememberPaymentCardToken, onRememberPaymentCardTokenChange, disabledChangeRememberPaymentCard, rememberCardLabel, onCancel }: DialogCreditCardConfirmProps) => {
  const isMobile = useResponsive('mobile')
  const isSubmittingRef = useRef(false)
  const lockedIsMobileRef = useRef(isMobile)
  /** 送出/3DS 期間凍結斷點，避免切換版型時 SDK 容器與 3DS 挑戰被重建而中斷授權。 */
  const dialogIsMobile = isSubmittingRef.current ? lockedIsMobileRef.current : isMobile
  /** 版型切換或改記住卡片時，SDK 需重掛。 */
  const remountKey = `${dialogIsMobile}|${context.rememberPaymentCardToken ?? false}`
  const { isSubmitting, hasError, isSdkReady, containerId, storedCardContainerId, submit, cancel, retry } = useCreditCardDialog({ context, mountCreditCardFields, submitAndExecute, cancelInFlight, onError, remountKey })
  isSubmittingRef.current = isSubmitting
  if (!isSubmitting) lockedIsMobileRef.current = isMobile
  const { t } = useTranslation('cartUi', { i18n: cartUiI18nInstance })
  const [isLeaveConfirmOpen, setIsLeaveConfirmOpen] = useState(false)

  const handleCloseAttempt = useCallbackEvent(() => {
    setIsLeaveConfirmOpen(true)
  })

  const handleStay = useCallbackEvent(() => {
    setIsLeaveConfirmOpen(false)
  })

  const handleLeave = useCallbackEvent(() => {
    setIsLeaveConfirmOpen(false)
    if (!cancel('user closed dialog')) return
    onCancel?.()
    onClose()
  })

  /** hasError 分支的「更換付款方式」：非使用者主動取消付款，不觸發 onCancel（避免誤發 checkout_cancel）。 */
  const handleChangePaymentMethod = useCallbackEvent(() => {
    if (!cancel('mount error, changing payment method')) return
    onClose()
  })

  // 付款鈕恆 enabled，點擊才檢查：卡片無效時只觸發 SDK 顯示欄位錯誤、不進送出流程（避免無 callback 卡死）。
  const handlePayClick = useCallbackEvent(() => {
    if (!isCreditCardValid) {
      revealCardValidationErrors()
      return
    }
    submit()
  })

  const dialogInner = (
    <>
      <DialogHeader title={t('cartUi.creditCardConfirmTitle', { defaultValue: '填寫信用卡資料' })} />
      <DialogContent rootStyle={hasError ? undefined : styles.contentNoBottomPadding}>
        <VStack gap={2} alignItems="flex-start">
          {/* 就緒前以 skeleton 佔位；容器須常駐 DOM 供 SDK 掛入，key 固定避免 skeleton 出現/消失時 keyless 重映射換掉已掛入的 node。 */}
          {!isSdkReady && !hasError && <Skeleton variant="rounded" animation="wave" height={CARD_FIELDS_MIN_HEIGHT_PX} width="100%" />}
          <div
            key={storedCardContainerId}
            id={storedCardContainerId}
            data-testid={storedCardContainerId}
            {...stylex.props(styles.mount, (isSdkReady || hasError) && styles.collapseWhenEmpty)}
          />
          <div
            key={containerId}
            id={containerId}
            data-testid={containerId}
            {...stylex.props(styles.mount, (isSdkReady || hasError) && styles.collapseWhenEmpty)}
          />
          {canRememberPaymentCardToken && isSdkReady && (
            <Checkbox
              label={rememberCardLabel}
              labelColor={colors.grey600}
              color={disabledChangeRememberPaymentCard ? 'grey' : 'blue'}
              checked={context.rememberPaymentCardToken ?? false}
              disabled={disabledChangeRememberPaymentCard}
              onChange={event => onRememberPaymentCardTokenChange?.(event.target.checked)}
            />
          )}
          {hasError && (
            <VStack alignItems="center" gap={4} rootStyle={styles.errorBox}>
              <PaymentFallbackIcon />
              <Text variant="body" color={colors.grey700} align="center" rootStyle={styles.errorText}>
                {t('cartUi.paymentFallbackMessage', { defaultValue: '付款發生問題，請確認網路連線正常後重新嘗試，或選擇其他付款方式。' })}
              </Text>
            </VStack>
          )}
        </VStack>
      </DialogContent>
      {(isSdkReady || hasError) && (
        <DialogActions>
          {hasError && !isSdkReady
            ? (
              <>
                <Button variant="outlined" onClick={handleChangePaymentMethod} fullWidth>
                  {t('cartUi.changePaymentMethod', { defaultValue: '更換付款方式' })}
                </Button>
                <Button onClick={retry} fullWidth>
                  {t('cartUi.retryPayment', { defaultValue: '重新再試' })}
                </Button>
              </>
            )
            : (
              <>
                {/* 確認框開啟時停用取消鈕：pets-ui Modal 無 focus trap，鍵盤焦點仍可穿透至被覆蓋的按鈕。 */}
                <Button variant="outlined" color="grey" onClick={handleCloseAttempt} disabled={isSubmitting || isLeaveConfirmOpen} fullWidth>
                  {t('cartUi.cancelPayment', { defaultValue: '取消付款' })}
                </Button>
                {isSdkReady && (
                  <Button onClick={handlePayClick} loading={isSubmitting} fullWidth>
                    {t('cartUi.pay', { defaultValue: '付款' })}
                  </Button>
                )}
              </>
            )}
        </DialogActions>
      )}
    </>
  )

  return (
    <>
      {dialogIsMobile
        ? (
          <Drawer open direction="bottom" height="auto" xStyles={{ base: { paper: styles.drawerPaper } }}>
            {dialogInner}
          </Drawer>
        )
        : (
          <Dialog open fullWidth maxWidth={480} height="auto">
            {dialogInner}
          </Dialog>
        )}
      {isLeaveConfirmOpen && <DialogPaymentLeaveConfirm onStay={handleStay} onLeave={handleLeave} />}
    </>
  )
}
